export type ArrayNumberPanel = { id: number; groupId: number; x: number; y: number };

// `Object.values()` on an object keyed by groupId sorts integer keys ascending.
// Group ids come from Pixi's uid counter, which starts over on each page load,
// while saved group ids stay large. Arrays drawn after a reload can therefore
// get the smallest group ids and display as 1, 2, … while older arrays shift up.
//
// Panel `id` is assigned when the panel is created (and, on load, in saved
// draw order) and is not replaced when a new array receives a low group id.
// The smallest panel id in a group is the draw-order key.
export function topLeftCoordsByArray(
  panels: ArrayNumberPanel[],
  orderByDrawnOrder: boolean,
): Array<{ x: number; y: number }> {
  if (orderByDrawnOrder) {
    const acc = new Map<number, { x: number; y: number; firstId: number }>();
    panels.forEach(({ id, groupId, x, y }) => {
      const roundedX = Math.round(x);
      const roundedY = Math.round(y);
      const existing = acc.get(groupId);
      if (!existing) {
        acc.set(groupId, { x: roundedX, y: roundedY, firstId: id });
        return;
      }
      if (id < existing.firstId) existing.firstId = id;
      if (roundedY < existing.y) {
        existing.y = roundedY;
        existing.x = roundedX;
      } else if (roundedY === existing.y && roundedX < existing.x) {
        existing.x = roundedX;
      }
    });
    return Array.from(acc.values())
      .sort((a, b) => a.firstId - b.firstId)
      .map(({ x, y }) => ({ x, y }));
  }

  return Object.values(
    panels.reduce(
      (acc, { groupId, x, y }) => {
        // y(decimal value) is not same for all panels in a same row & Similarly for column.
        // Hence ignoring the precision(decimal value) assuming it doesn't impact.
        const roundedX = Math.round(x);
        const roundedY = Math.round(y);
        acc[groupId] = acc[groupId] ?? { x: roundedX, y: roundedY };
        if (roundedY < acc[groupId].y) {
          acc[groupId].y = roundedY;
          acc[groupId].x = roundedX;
        }
        acc[groupId].x = roundedY === acc[groupId].y ? Math.min(acc[groupId].x, roundedX) : acc[groupId].x;
        return acc;
      },
      {} as { [groupId: number]: { x: number; y: number } },
    ),
  );
}
